import * as React from 'react'

import dynamic from 'next/dynamic'
import withHydrationOnDemand from 'react-hydration-on-demand'

import {
  useSiteDefinition,
  useSiteConfig,
  i18n,
  MessageArea,
  Routes,
  LIST_EXTENSION,
  useEnterpriseContext,
  useTransmit,
  useRequestConfig,
} from '@thg-commerce/enterprise-core'
import { sanitizeParameter } from '@thg-commerce/enterprise-utils'
import { AriaMessageType } from '@thg-commerce/gravity-elements'
import { Header } from '@thg-commerce/gravity-patterns/Header/Header'
import { InstantSearchProps, NewHeader } from '@thg-commerce/gravity-patterns'
import { styled, zIndex, ZIndexLevel } from '@thg-commerce/enterprise-theme'
import { getColumboEventData } from '@thg-commerce/enterprise-metrics'
import { pushToDataLayer } from '@thg-commerce/enterprise-metrics/src/data_layer'
import { HeaderSlotType } from '@thg-commerce/enterprise-config'

import { SessionSettingsToast } from '../ToastBanner'
import { WishlistNotification } from '../WishlistNotification'
import { AccountNav as AccountNavComponent } from '../AccountNav'
import { withBasket } from './WithBasket'
import { SessionSettingsModalFactory } from './SessionSettingsModal'

export interface HeaderGroupProps {
  wishlistNotification: boolean
  stickyHeader: boolean
  flagPath: string
  userAgent: string
  isMobile: boolean
  headerData?: any
  brandsData?: any
  children?: JSX.Element | JSX.Element[]
}

const CookieModalContainer = styled.div`
  display: flex;
  position: fixed;
  bottom: 0;
  padding: 0;
  margin: 0px;
  width: 100%;
  ${zIndex(ZIndexLevel.Highest)}
`

const AccountNav = withHydrationOnDemand({
  on: [
    [
      'hover',
      () => document.querySelector("[data-testid='account-trigger-wrapper']"),
    ],
  ],
})(AccountNavComponent)

const InstantSearchInjector = dynamic<{
  children: (props: InstantSearchProps) => React.ReactNode
}>(() =>
  import('./InstantSearchInjector').then((mod) => mod.InstantSearchInjector),
)

const SkipLinkComponent = dynamic<{}>(
  () => import('../SkipLink').then((mod) => mod.SkipLink),
  { ssr: true },
)

const SkipLink = withHydrationOnDemand({
  on: [['delay']],
})(SkipLinkComponent)

export const HeaderGroup = (props: HeaderGroupProps) => {
  const transmit = useTransmit()
  const headerRef = React.useRef<HTMLElement | null>(null)

  const {
    defaultLocale,
    siteName,
    currencies,
    subsite,
    siteId,
    channel,
  } = useSiteDefinition()
  const {
    hasDynamicHeaderDropdown,
    countryDomainMap,
    hasSessionSettings,
    showResponsiveSubMenuFirstLink,
    showResponsiveSubMenuSecondLink,
    showResponsiveSubMenuThirdLink,
    showResponsiveSubMenuFourthLink,
    headerSlots,
    submenuSlots,
    hasWaterfallMenu,
    responsiveFlyoutMenu_hasStoreIcon,
    flyoutMenuShowWishlistIcon,
    secondHeaderSlotUrl,
    newHeader,
    newHeaderUseAlternateGrid,
    showExposedSearchBarOnMobile,
  } = useSiteConfig()
  const requestConfig = useRequestConfig()

  React.useEffect(() => {
    function cb(e: MouseEvent) {
      const target = (e.target as HTMLElement).closest(
        'header a',
      ) as HTMLAnchorElement | null

      if (target) {
        pushToDataLayer({
          type: 'elysiumEvent',
          eventData: {
            eventAction: `Clicked ${target.innerText}`,
            eventCategory: 'Navigation Header',
            eventLabel: target.href,
          },
        })
      }
    }

    headerRef.current?.addEventListener('click', cb)
    return () => headerRef.current?.removeEventListener('click', cb)
  })

  const searchI18nText = {
    productsTitle: i18n('header.search.products.title'),
    searchResultsAria: i18n('header.search.results'),
    suggestionsTitle: i18n('header.search.suggestions.title'),
    correctionsTitle: i18n('header.search.corrections.title'),
    resultsAvailable: i18n('header.search.results.available'),
    lastResult: i18n('header.search.results.last'),
    fromText: i18n('product.price.from.text'),
  }

  const i18nText = {
    searchPlaceholder: i18n('header.search.placeholder'),
    logoAriaLabel: i18n('header.logo.arialabel', siteName),
    accountLabel: i18n('header.account.label'),
    basketLabel: i18n('header.basket.label'),
    searchButtonAriaLabel: i18n('header.search.button.arialabel'),
    menuButtonAriaLabel: i18n('header.menu.button.arialabel'),
    closeButtonAriaLabel: i18n('general.modal.close.button.arialabel'),
    clearAriaLabel: i18n('input.clear.arialabel'),
    basketButtonAriaLabel: i18n('header.basket.menu.arialabel'),
    accountButtonAriaLabel: i18n('header.account.nav.arialabel'),
    headerAriaLabel: i18n('header.arialabel'),
    wishlistAriaLabel: i18n('account.wishlist.view'),
    slotText: [
      {
        text: i18n('header.slot1.text'),
        url: i18n('header.slot1.link'),
      },
      {
        text: i18n('header.slot2.text'),
        url: secondHeaderSlotUrl,
      },
      {
        text: i18n('header.wishlist.slot.text'),
        url: i18n('header.wishlist.slot.link'),
      },
    ],
  }

  const submenuI18nText = {
    sessionSettingsChangeText: i18n('header.submenu.button.change.text'),
    sessionSettingsYourSettingsAriaLabel: i18n(
      'header.sessionsettings.modal.header',
    ),
    sessionSettingsButtonOpenAriaLabel: i18n(
      'header.sessionsettings.button.open.arialabel',
    ),
    linkOneText: i18n('header.submenu.link1.text'),
    linkOneHref: i18n('header.submenu.link1.link'),
    linkTwoText: i18n('header.submenu.link2.text'),
    linkTwoHref: i18n('header.submenu.link2.link'),
    ariaLabel: i18n('header.submenu.arialabel'),
    slotText: submenuSlots
      ?.filter(
        (submenuSlot) =>
          submenuSlot.type === HeaderSlotType.LINK &&
          submenuSlot.textProperty &&
          submenuSlot.urlProperty,
      )
      .map((slot) => ({
        text: i18n(`header.${slot.textProperty}`),
        url: 'urlProperty' in slot ? i18n(`header.${slot.urlProperty}`) : '',
      })),
  }

  const flyoutI18nText = {
    homeLabel: i18n('header.logo.arialabel', siteName),
    sectionHome: i18n('header.flyout.sectionhome.text'),
    linkWithChildrenLabel: i18n('header.flyout.linkwithchildren.arialabel'),
    close: i18n('general.modal.close.button.arialabel'),
    back: i18n('header.flyout.button.back.text'),
    wishlist: i18n('account.wishlist.view'),
  }

  const headerDropdownI18nText = {
    closeButtonAriaLabel: i18n('general.modal.close.button.arialabel'),
  }

  const sessionSettingsModalI18nText = {
    header: i18n('header.sessionsettings.modal.header'),
    shippingLabel: i18n('header.sessionsettings.selector.shipping.to.label'),
    regionLabel: i18n('header.sessionsettings.selector.region.label'),
    currenciesLabel: i18n('header.sessionsettings.selector.currencies.label'),
    submitButtonText: i18n(
      'header.sessionsettings.selector.submit.button.text',
    ),
  }

  const EnterpriseCtx = useEnterpriseContext()

  const setHeaderHeightContext = (height: number) => {
    const {
      value: [_, setHeaderHeight],
    } = EnterpriseCtx.headerHeightRef
    setHeaderHeight(height)
  }

  const renderAnnouncer = (ariaLive: AriaMessageType, message: string) => (
    <MessageArea aria-live={ariaLive} message={message} />
  )

  const onSearchSubmit = (searchText: string) => {
    window.location.href = `${Routes.Search}?search=${
      searchText.length > 0 ? sanitizeParameter(searchText) : ''
    }`
  }

  const searchFocusCallback = (event: React.FocusEvent) => {
    event.stopPropagation()

    const propertyArgs = {
      defaultLocale,
      subsite,
      siteId,
      channel,
      customerLocation: EnterpriseCtx.currentLocation,
    }

    const argumentsObj = {}
    for (const [_, attribute] of Object.entries(event.target.attributes)) {
      argumentsObj[attribute.name] = attribute.value
    }

    transmit({
      type: 'columbo',
      payload: getColumboEventData({
        propertyArgs,
        argumentsObj,
        requestArgs: {
          client_timestamp: Math.round(Date.now() / 1000),
          url: window.location.href,
        },
        eventData: {
          type: 'input-focus',
          subtype: 'headerSearch_input',
          contents: [{ widget_id: 0 }],
        },
        nonce: EnterpriseCtx.metricNonce,
      }),
    })
  }

  const sessionSettingsModal = SessionSettingsModalFactory(
    sessionSettingsModalI18nText,
    countryDomainMap,
    currencies,
  )

  const headerProps = {
    withBasket,
    ...{
      defaultLocale,
      i18nText,
      flyoutI18nText,
      headerDropdownI18nText,
      hasDynamicHeaderDropdown,
      hasWaterfallMenu,
      headerRef,
      isMobileApp: requestConfig.isMobileApp || false,
      isAmp: false,
      useAlternateHeaderGrid: newHeaderUseAlternateGrid || false,
      isMobile: props.isMobile,
      userAgent: props.userAgent,
      accountNav: () => <AccountNav />,
      responsiveFlyoutMenuIcons: {
        store: responsiveFlyoutMenu_hasStoreIcon,
        wishlist: flyoutMenuShowWishlistIcon,
      },
      headerSearch: {
        searchI18nText,
        renderAnnouncer,
        InstantSearchInjector,
        onSubmit: onSearchSubmit,
        autocompleteLink: `${Routes.Search}?autocomplete=`,
        currency: EnterpriseCtx.currency,
        shippingDestination: EnterpriseCtx.shippingDestination.code,
        onFocus: searchFocusCallback,
        showExposedSearch: showExposedSearchBarOnMobile || false,
      },
      submenu: {
        sessionSettingsModal,
        flagPath: props.flagPath,
        sessionSettingsTriggerText: `${EnterpriseCtx.subsite} - ${EnterpriseCtx.currency}`,
        showSessionSettings: hasSessionSettings,
        i18nText: submenuI18nText,
        showLinkOne: showResponsiveSubMenuFirstLink,
        showLinkTwo: showResponsiveSubMenuSecondLink,
        showLinkThree: showResponsiveSubMenuThirdLink,
        showLinkFour: showResponsiveSubMenuFourthLink,
        submenuSlots: submenuSlots || [],
      },
      headerSlots: headerSlots || [],
      setHeight: setHeaderHeightContext,
      navigationTree: props.headerData?.header,
      logoLink: props.headerData?.homepage?.path ?? '/',
      brands: {
        brandsData: props.brandsData?.brands,
        i18nText: {
          viewAllBrands: i18n('header.brands.view.all.text'),
          navAriaLabel: i18n('header.brands.nav.aria.label'),
        },
        viewAllBrandsLink: Routes.Brands,
      },
      sticky: props.stickyHeader,
      routes: {
        accountHome: Routes.AccountHome,
        basket: Routes.Basket,
        listExtension: LIST_EXTENSION,
        wishlist: Routes.Wishlists,
      },
    },
    ...(props.wishlistNotification && {
      wishlistNotification: WishlistNotification,
    }),
  }

  return (
    <React.Fragment>
      <CookieModalContainer
        id="cookie-modal-container"
        ref={EnterpriseCtx.cookieModalContainerRef}
      />
      <SkipLink />
      {newHeader ? (
        <NewHeader {...headerProps} />
      ) : (
        <Header {...headerProps} data-testid="header" />
      )}
      {props.children}
      <SessionSettingsToast />
    </React.Fragment>
  )
}
